'use client';

import { useEffect, useState } from 'react';
import { Card, Switch, Text, Title } from '@tremor/react';
import { useToast } from '../../../utils/useToast';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import {
  useWorkspaceSettings,
  workspaceSettingsQueryKey,
} from './useWorkspaceSettings';

const REACT_APP_API_HOST = process.env.REACT_APP_API_HOST;

const features = [
  {
    id: 'timeoffEnabled',
    name: 'Time Off',
    description: 'Allow employees to request, approve, and track time away.',
  },
  {
    id: 'rotaManagement',
    name: 'Rota Management',
    description: 'Enable scheduling and tracking of employee shifts.',
  },
  {
    id: 'reports',
    name: 'Reporting',
    description: 'Access detailed reports and analytics on various HR metrics.',
  },
  {
    id: 'ats',
    name: 'Applicant Tracking System (ATS)',
    description: 'Manage job postings and track applicants through the hiring process.',
  },
  {
    id: 'timesheets',
    name: 'Timesheets',
    description: 'Allow employees to log their work hours and track time.',
  },
  {
    id: 'peopleManagement',
    name: 'People Management',
    description: 'Manage employee profiles, directory, and organization structure.',
  },
  {
    id: 'documentManagement',
    name: 'Document Management',
    description: 'Store and manage company and employee documents securely.',
  },
];

export function FeatureManagement() {
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useWorkspaceSettings();
  const enabledFeatures = data?.features;

  useEffect(() => {
    if (!error) return;

    let errorMessage = 'Failed to load workspace settings.';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message || errorMessage;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast({ title: "Error Loading Settings", description: errorMessage, variant: "error" });
  }, [error, toast]);

  const handleToggle = async (featureId: string, checked: boolean) => {
    const originalState = queryClient.getQueryData<Record<string, boolean>>(workspaceSettingsQueryKey);
    const featureName = features.find(f => f.id === featureId)?.name || featureId;

    queryClient.setQueryData<Record<string, boolean>>(workspaceSettingsQueryKey, (oldData) => ({
      ...(oldData || {}),
      [featureId]: checked,
    }));

    setIsUpdating((prev) => ({ ...prev, [featureId]: true }));

    const loadingToast = toast({
      title: "Updating...",
      description: `Updating feature '${featureName}'...`,
      variant: "loading",
      duration: Infinity
    });

    try {
      const response = await axios.patch(`${REACT_APP_API_HOST}/v1/workspace`, { [featureId]: checked });
      const result = response.data;
      loadingToast.dismiss();

      if (result.status !== 'UPDATE_WORKSPACE_SETTINGS_SUCCESS') {
        const backendErrorMessage = result.message || 'Backend indicated update failure.';
        console.error("API did not return success status:", result);
        throw new Error(backendErrorMessage);
      }

      toast({
        title: "Success",
        description: `Feature '${featureName}' ${checked ? 'enabled' : 'disabled'}.`,
        variant: "success",
      });

      await queryClient.invalidateQueries(workspaceSettingsQueryKey);
      await queryClient.invalidateQueries('config');

    } catch (error) {
      loadingToast.dismiss();
      console.error(`Error updating feature ${featureId}:`, error);
      let errorMessage = 'Unknown error occurred';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message || 'Failed to update feature setting';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Update Failed",
        description: `Failed to update '${featureName}': ${errorMessage}`,
        variant: "error",
      });

      if (originalState) {
        queryClient.setQueryData(workspaceSettingsQueryKey, originalState);
      }
    } finally {
      setIsUpdating((prev) => ({ ...prev, [featureId]: false }));
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <Title>Feature Management</Title>
        <Text>Loading feature settings...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-6">
        <Title>Feature Management</Title>
        <Text>Error loading settings. Please try again later.</Text>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <Title>Feature Management</Title>
      <Text>Enable or disable specific modules for your workspace.</Text>

      <div className="mt-6 space-y-6">
        {features.map((feature) => (
          <div
            key={feature.id}
            className="flex items-center justify-between space-x-4 rounded-tremor-default border border-tremor-border p-4 dark:border-dark-tremor-border"
          >
            <div>
              <h4 className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                {feature.name}
              </h4>
              <p className="mt-1 text-tremor-default leading-6 text-tremor-content dark:text-dark-tremor-content">
                {feature.description}
              </p>
            </div>
            <Switch
              id={feature.id}
              name={feature.name}
              checked={enabledFeatures?.[feature.id] || false}
              onChange={(checked) => handleToggle(feature.id, checked)}
              color="rose"
              disabled={isUpdating[feature.id]}
            />
          </div>
        ))}
      </div>
    </Card>
  );
} 
